import { ViewType } from "../Models/AppModel.js";
import { bindedModelBase } from "../Models/BindedModel.js";
import { ToolType, ToolUtils } from "./ITool.js";
/**
 * Attribute used to save a variable.
 * Supported type: Vector3, primitive, Euler, float32BufferAttribute, Uint16BufferAttribute
 * @param keys All properties to save example "uniforms", "uniforms.colors".
 */
export function Save(...keys) {
    return function (target, propertyKey, descriptor) {
        target.__saveKeys ?? (target.__saveKeys = {});
        if (keys.length > 0) {
            for (const key of keys) {
                target.__saveKeys[`${propertyKey}.${key}`] = true;
            }
        }
        else {
            target.__saveKeys[propertyKey] = true;
        }
    };
}
/**
 * Attribute used to save a variable when also children use Save or SaveChildren attribute.
 * Supported type: Vector3, primitive, Euler, float32BufferAttribute, Uint16BufferAttribute
 * @param keys All properties to save example "uniforms", "uniforms.colors".
 */
export function SaveChild(...keys) {
    return function (target, propertyKey, descriptor) {
        target.__saveKeysChildren ?? (target.__saveKeysChildren = {});
        target.__saveKeysChildren[propertyKey] = true;
        if (keys.length > 0) {
            target.__saveKeys ?? (target.__saveKeys = {});
            for (const key of keys) {
                target.__saveKeys[`${propertyKey}.${key}`] = true;
            }
        }
    };
}
/**
 * Manages the save and restore.
 */
export class SaveHandlerBase {
    constructor(main, context) {
        this.cacheTools = [];
        this._context = context;
        this._main = main;
    }
    resetModel() {
        this._model = {
            bindedModel: undefined,
            state: undefined,
            ap: { tools: [] },
            lt: { tools: [] },
            appStateModel: undefined
        };
    }
    /**
     * Generates the model to save from all tools that use Save and SaveChild attributes.
     */
    getUpdatedData() {
        this.resetModel();
        this._context.state._context = undefined; //hack to avoid circular dependencies
        this._context.state._caseModel = undefined; //hack to avoid to save useless data
        this._model.state = JSON.stringify(this._context.state);
        this._context.state._context = this._context; //hack to avoid circular dependencies
        this._context.state._caseModel = this._main.caseModel; //hack to avoid to save useless data
        const layers = this._main.model._layers; // TODO we can remove this? is no more present
        this._main.model._context = undefined; //hack to avoid circular dependencies
        this._main.model._layers = undefined;
        this._main.model._history = undefined;
        this._model.appStateModel = JSON.stringify(this._main.model);
        this._main.model._context = this._context; //hack to avoid circular dependencies
        this._main.model._layers = layers;
        this._main.model._history = this._main.history;
        //hack to avoid to save useless data
        const appModel = JSON.parse(this._model.appStateModel);
        appModel.isMeasurementToolSelectedAP = undefined;
        appModel.isMeasurementToolSelectedLT = undefined;
        this.onBeforeAppModelStringfy && this.onBeforeAppModelStringfy(appModel);
        this._model.appStateModel = JSON.stringify(appModel);
        const bindedModelCloned = JSON.parse(JSON.stringify(bindedModelBase));
        this.onBeforeBindedModelStringfy && this.onBeforeBindedModelStringfy(bindedModelCloned);
        this._model.bindedModel = JSON.stringify(bindedModelCloned);
        this.updateTools(ViewType.AP);
        this.updateTools(ViewType.LT);
        return this._model;
    }
    /**
     * Inits and restore tools.
     */
    async restoreData(restoreData, cacheData) {
        const dataToRestore = restoreData || cacheData;
        if (!dataToRestore)
            return;
        let bindedModelToRestore;
        if (restoreData) {
            bindedModelToRestore = JSON.parse(restoreData.bindedModel);
            //freeze binding
            for (const key in bindedModelToRestore) {
                bindedModelBase[key] = bindedModelToRestore[key];
            }
            //unfreeze binding and compute
            const appStateToRestore = JSON.parse(restoreData.appStateModel);
            for (const key in appStateToRestore) {
                this._main.model[key] = appStateToRestore[key];
            }
        }
        const linesMaskAP = bindedModelBase.linesMaskAP;
        bindedModelBase.linesMaskAP = 0; // To generate correct indexes
        const linesMaskLT = bindedModelBase.linesMaskLT;
        bindedModelBase.linesMaskLT = 0;
        const goniometersMaskAP = bindedModelBase.goniometersMaskAP;
        bindedModelBase.goniometersMaskAP = 0;
        const goniometersMaskLT = bindedModelBase.goniometersMaskLT;
        bindedModelBase.goniometersMaskLT = 0;
        const meshesAP = dataToRestore.ap.tools;
        for (const meshToRestore of meshesAP) {
            if (!!restoreData || this.cacheTools.includes(meshToRestore.type)) { // prevent incorrect tool restoring from cache model
                this.restoreMeshData(this.initMesh(this._main.toolsAP, meshToRestore.type, ViewType.AP, meshToRestore.data), meshToRestore.data);
            }
        }
        const meshesLT = dataToRestore.lt.tools;
        for (const meshToRestore of meshesLT) {
            if (!!restoreData || this.cacheTools.includes(meshToRestore.type)) { // prevent incorrect tool restoring from cache model
                this.restoreMeshData(this.initMesh(this._main.toolsLT, meshToRestore.type, ViewType.LT, meshToRestore.data), meshToRestore.data);
            }
        }
        bindedModelBase.linesMaskAP = linesMaskAP;
        bindedModelBase.linesMaskLT = linesMaskLT;
        bindedModelBase.goniometersMaskAP = goniometersMaskAP;
        bindedModelBase.goniometersMaskLT = goniometersMaskLT;
    }
    initMesh(tools, meshType, viewType, meshData) {
        switch (meshType) {
            case ToolType.plane:
                return tools.plane;
            case ToolType.goniometer:
                return this._main.toolsInit.initCrossGoniometer(tools, false);
            case ToolType.line:
                return this._main.toolsInit.initLine(tools, false);
            case ToolType.contralateralMechanicalAxis:
                return (viewType === ViewType.AP && this._main.toolsInit.initControlaterMechanicalAxis()) || undefined;
            case ToolType.mechanicalAxis:
                return viewType === ViewType.AP ? this._main.toolsInit.initMechanicalAxisAP() : this._main.toolsInit.initMechanicalAxisLT();
            case ToolType.anatomicalAxis: {
                return viewType === ViewType.AP ? this._main.toolsInit.initAnatomicalAxisView(ViewType.AP) : this._main.toolsInit.initAnatomicalAxisView(ViewType.LT);
            }
            default:
                console.error("Cannot restore mesh: cannot recognize type. Add it on 'restoreMesh' mesh method.");
        }
        return undefined;
    }
    restoreMeshData(obj, dataToRestore) {
        if (obj) {
            this.triggerCallback("onBeforeRestore", obj, dataToRestore);
            ToolUtils.restore(obj, dataToRestore);
            this.triggerCallback("onAfterRestore", obj);
        }
    }
    triggerCallback(callbackName, obj, args) {
        obj[callbackName] && obj[callbackName](args);
        if (obj.__saveKeysChildren !== undefined) {
            for (const key in obj.__saveKeysChildren) {
                obj[key] && this.triggerCallback(callbackName, obj[key]); // TODO better cast
            }
        }
    }
    updateTools(viewType) {
        const toolsToSave = viewType === ViewType.AP ? this._model.ap.tools : this._model.lt.tools;
        const tools = this._main.getTools(viewType);
        for (const tool of [
            tools.plane,
            ...tools.lines,
            ...tools.goniometers,
            tools.mechanicalAxis,
            tools.anatomicalAxis,
            tools.contralateralMechanicalAxis
        ]) {
            const toolToSave = ToolUtils.getStateToRestore(tool);
            toolToSave && toolsToSave.push(toolToSave);
        }
    }
}
//# sourceMappingURL=Save.js.map